import type { WalkthroughSessionEntity } from '@unifyapps/defs/types/walkthroughSession';
import { useCallback, useMemo } from 'react';
import { useWalkthroughManagerStore } from '../../stores/WalkthroughManagerStore';
import { createWalkthroughInstanceEventBus } from '../../types/walkthroughInstanceEventBus';
import { useWalkthroughSessionClient } from './useWalkthroughSessionClient';

/**
 * Encapsulates session persistence and walkthrough change notification.
 * Used by useWalkthroughOrchestrator to keep the hook focused on orchestration logic.
 */
export function useWalkthroughSessionSync() {
  const store = useWalkthroughManagerStore();
  const { updateSession } = useWalkthroughSessionClient();
  const { setActiveSession } = store.getState().conductorActions;

  const instanceId = store.getState().instanceId;
  const walkthroughInstanceEventBus = useMemo(
    () => createWalkthroughInstanceEventBus(instanceId),
    [instanceId],
  );

  // Notify other parts of the app that walkthrough state has changed.
  const notifyWalkthroughChange = useCallback(
    (pageId: string) => {
      walkthroughInstanceEventBus.publish('notifyWalkthroughChange', { pageId });
    },
    [walkthroughInstanceEventBus],
  );

  // Helper to persist the walkthrough session in RUNNER mode.
  const saveSessionToBackend = useCallback(
    async (session: WalkthroughSessionEntity) => {
      try {
        const updatedSession = await updateSession(session);
        if (!updatedSession) {
          console.error('Failed to update walkthrough session');
          return null;
        }
        setActiveSession(updatedSession);
        return updatedSession;
      } catch (error) {
        console.error('Failed to save session to backend:', error);
        return null;
      }
    },
    [updateSession, setActiveSession],
  );

  return {
    saveSessionToBackend,
    notifyWalkthroughChange,
    setActiveSession,
    walkthroughInstanceEventBus,
  };
}
